import { css } from "@emotion/react";
import React, { useMemo } from "react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { range } from "../@utils/range";
import { CalendarDayProps } from "./CalendarDay";
import { CalendarDayEvent } from "./CalendarDayEvent";
import { CalendarDay } from "./CalendarDay";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const isDateOutsidePeriod = (
  date: dayjs.Dayjs,
  min?: dayjs.ConfigType,
  max?: dayjs.ConfigType,
) => {
  return (
    (min !== undefined && min !== null && date.isSameOrBefore(min, "date")) ||
    (max !== undefined && max !== undefined && date.isSameOrAfter(max, "date"))
  );
};

const getEvent = (date: dayjs.Dayjs, events?: CalendarEvent[]) => {
  return events?.find((event) => {
    if (event.start === undefined) {
      return date.isSameOrBefore(event.end, "date");
    }
    if (event.end === undefined) {
      return date.isSameOrAfter(event.start, "date");
    }
    return (
      date.isSameOrAfter(event.start, "date") &&
      date.isSameOrBefore(event.end, "date")
    );
  });
};

export interface CalendarDaysProps extends Omit<
  CalendarDayProps,
  "date" | "disabled" | "event"
> {
  children?: React.ReactNode;
}

export interface CalendarEvent {
  start?: dayjs.ConfigType;
  end?: dayjs.ConfigType;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  type?: "default" | "disabled" | string;
  props?: CalendarDaysProps;
}

export interface CalendarDaysInMonthProps<T> {
  children?: never;
  events?: CalendarEvent[];
  /**
   * datesで渡されたデータをどのようにレンダリングするかを指定します
   * @default "(message: string) => <Centering>{new String(message)}</Centering>""
   */
  renderCell?: (props: T) => React.ReactNode;
  /**
   * keyに対応するカレンダーのセルに任意の子要素を入れることができます。
   * keyのフォーマットはYYYYMMDDでなければなりません。
   */
  dates?: Record<string, T>;
  daysProps?: CalendarDaysProps;
  date?: dayjs.ConfigType;
  min?: dayjs.ConfigType;
  max?: dayjs.ConfigType;
  /**
   * カレンダーのセルの高さを指定します。
   * これが指定されていない場合、カレンダーのセルは正方形になります。
   */
  cellHeight?: number | string;
  /**
   * 日付がクリックされた場合に、その日付とイベントを返す。
   */
  onClickDay?: (
    value: Date,
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;
}

const getFirstWeekday = (date: dayjs.ConfigType) => {
  return dayjs(date).startOf("month").day();
};
const getDaysCount = (date: dayjs.ConfigType) => {
  return dayjs(date).daysInMonth() + 1;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const defaultRenderCell = (message: any) => (
  <div
    css={css`
      margin: 0 auto;
    `}
  >
    {message}
  </div>
);

export const CalendarDaysInMonth = <T,>({
  dates,
  events = [],
  daysProps = {},
  date = dayjs(),
  min,
  max,
  cellHeight,
  renderCell = defaultRenderCell,
  onClickDay,
  ...props
}: Readonly<CalendarDaysInMonthProps<T>>) => {
  const firstWeekday = useMemo(() => getFirstWeekday(date), [date]);
  const days = useMemo(() => getDaysCount(date), [date]);
  const reversedEvents = useMemo(() => events.slice().reverse(), [events]);
  return (
    <div
      css={[
        css`
          display: grid;
          grid-template-columns: repeat(7, minmax(42px, 1fr));
        `,
        {
          gridAutoRows: cellHeight ?? "1fr",
        },
        cellHeight === undefined
          ? css`
              & > *:first-of-type {
                aspect-ratio: 1;
              }
            `
          : null,
      ]}
      {...props}
    >
      {range(firstWeekday).map((i) => (
        <div key={`empty__${i}`} />
      ))}
      {range(1, days).map((day) => {
        const d = dayjs(date).date(day);
        const event = getEvent(d, reversedEvents);
        const disabled = isDateOutsidePeriod(d, min, max);
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        const { props: eventProps } = event || ({ props: {} } as CalendarEvent);

        const dateProps = dates?.[d.format("YYYYMMDD")];
        const dateChildren =
          dateProps !== undefined &&
          dateProps !== null &&
          renderCell(dateProps);
        if (
          eventProps?.children !== undefined &&
          eventProps?.children !== null
        ) {
          const { children, ...eventProps_ } = eventProps;
          return (
            <CalendarDay
              date={d}
              key={d.format()}
              disabled={disabled}
              event={event}
              onClickDay={onClickDay}
              {...daysProps}
              {...eventProps_}
            >
              <CalendarDayEvent
                start={dayjs(event?.start)}
                end={dayjs(event?.end)}
                date={d}
                title={typeof children === "string" ? children : undefined}
              >
                {children}
              </CalendarDayEvent>
              {dateChildren}
            </CalendarDay>
          );
        } else {
          return (
            <CalendarDay
              date={d}
              key={d.format()}
              disabled={disabled}
              event={event}
              onClickDay={onClickDay}
              {...daysProps}
              {...eventProps}
            >
              {dateChildren}
            </CalendarDay>
          );
        }
      })}
    </div>
  );
};
